// Who is draftable. BACKLOG D4, DRAFT_MODES §6.2.
//
// "A pool is a declarative filter, resolved against the fact store when the
// draft is prepared, producing both the draftable set and the house ranking
// over it."
//
// WHY A RULE AND NOT AN ASSUMPTION. Every draft until now has assumed the pool
// is "every player in the directory", and that assumption is welded into the
// place it is made. There has to be room for challenge modes, such as a quick
// weekend game, and the worked example — the
// starters playing that weekend — is not a new acquisition, it is a filter over
// facts already stored. So the pool becomes a value a league carries, the
// default is `all`, and the feature stays invisible until somebody wants it.
//
// THE RULES THAT DO NOT EXIST YET SAY SO. `depth-chart-rank` and
// `playing-in-period` are in the shape because they are the two his example
// needs and because leaving them out would mean changing this type later; they
// are refused by the resolver rather than silently ignored, which is the
// difference between "not built" and "quietly did nothing".

import { z } from 'zod';
// ONE `SqlQuery`, not a second one that happens to match. `api/directory`
// already declares the shape every query builder in this package returns, and
// a duplicate would be two types with one name that drift apart quietly.
import { binaryCompare, type SqlQuery } from '../api/directory';
import type { DirectoryEntry } from '../api/artifacts';

export const PoolRule = z.discriminatedUnion('rule', [
  z.object({ rule: z.literal('positions'), in: z.array(z.string().min(1)).min(1) }),
  z.object({ rule: z.literal('clubs'), in: z.array(z.string().min(1)).min(1) }),
  /** Their club has a fixture that period. Needs the schedule; not resolved yet. */
  z.object({ rule: z.literal('playing-in-period'), period: z.number().int().min(1) }),
  /** 1 = listed starters only. Needs `depth.position`; not resolved yet. */
  z.object({ rule: z.literal('depth-chart-rank'), atMost: z.number().int().min(1) }),
  /** Not ruled out — no injury that keeps them off the field. */
  z.object({ rule: z.literal('available') }),
  /** A hand-made pool. The escape hatch, and the one that needs no facts. */
  z.object({ rule: z.literal('list'), playerIds: z.array(z.string().min(1)).min(1) }),
  /**
   * People with at most this many seasons in the pro league, never a club
   * unit, which has no seasons: `0` is a dynasty's rookie draft
   * (PLAN-2026-10-27-the-next-season §2.4 rule 2).
   */
  z.object({ rule: z.literal('experience'), atMost: z.number().int().min(0) }),
]);
export type PoolRule = z.infer<typeof PoolRule>;

export const PlayerPool = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('all') }),
  z.object({ kind: z.literal('filter'), rules: z.array(PoolRule).min(1).max(12) }),
]);
export type PlayerPool = z.infer<typeof PlayerPool>;

/** What every league gets, and what makes this invisible until somebody wants it. */
export const DEFAULT_POOL: PlayerPool = { kind: 'all' };

/** A rule this build cannot resolve yet, named. */
export class UnresolvablePool extends Error {
  constructor(public readonly rule: PoolRule['rule']) {
    super(`The pool rule "${rule}" is not resolved yet.`);
    this.name = 'UnresolvablePool';
  }
}

/**
 * The draftable players, as SQL.
 *
 * WHAT IS ALWAYS TRUE, whatever the rules say: the sport matches, the season
 * matches, and **the position is one this league's roster can actually hold**.
 * That last one is why `rosterPositions` is a parameter rather than something
 * this file works out — a league with no DST slot must not be offered defenses,
 * and only the caller knows the slot shape (the core never names a sport).
 *
 * `status = 'active'` IS ALSO ALWAYS TRUE and is not a rule. A retired player
 * is not a narrower pool, they are not a player this season; making it a rule
 * would let somebody build a pool that drafts them.
 */
export function poolQuery(
  pool: PlayerPool,
  sport: string,
  season: number,
  rosterPositions: readonly string[],
): SqlQuery {
  // THE SEEDED WORLD IS NEVER DRAFTABLE, AND THAT IS NOT A RULE EITHER. The
  // seeder writes its players into the same `players` table as the real ones
  // behind one flag — the vendor-key namespace the `sim` source stamps,
  // `sim` for its people and `sim-team` for its clubs (HANDOFF, "test as you
  // fly") — and nothing downstream of the projection read it. Found
  // 2026-09-23: the public ranking served 238 invented names and a real
  // league drafted two of them onto real rosters (AUDIT-2026-09-23 RD2). A
  // fixed condition beside `status = 'active'` rather than a pool rule,
  // because a rule is a thing somebody can leave out.
  const where: string[] = [
    `p.sport = ?1`,
    `p.status = 'active'`,
    // By path, as `worldClause` in the league store asks it: walking the keys
    // with `json_each` is a row read per key, and D1 bills them.
    `NOT (json_type(p.external_ids_json, '$."sim"') IS NOT NULL
          OR json_type(p.external_ids_json, '$."sim-team"') IS NOT NULL)`,
  ];
  const params: (string | number)[] = [sport];

  // Positions the roster can hold. `json_each` because `positions_json` is a
  // list and a player may have more than one — a RB/WR is draftable by a
  // league with either slot.
  const holdable = rosterPositions.map((_, i) => `?${params.length + i + 1}`).join(', ');
  params.push(...rosterPositions);
  where.push(
    `EXISTS (SELECT 1 FROM json_each(p.positions_json) WHERE json_each.value IN (${holdable}))`,
  );

  if (pool.kind === 'filter') {
    for (const rule of pool.rules) {
      switch (rule.rule) {
        case 'positions': {
          const holes = rule.in.map((_, i) => `?${params.length + i + 1}`).join(', ');
          params.push(...rule.in);
          where.push(
            `EXISTS (SELECT 1 FROM json_each(p.positions_json) WHERE json_each.value IN (${holes}))`,
          );
          break;
        }
        case 'clubs': {
          const holes = rule.in.map((_, i) => `?${params.length + i + 1}`).join(', ');
          params.push(...rule.in);
          where.push(`p.pro_team IN (${holes})`);
          break;
        }
        case 'available':
          // An injury that keeps somebody off the field. `injury_status` is a
          // `InjuryStatusDef` id and the severities are the sport's, so the
          // caller hands in which ones rule a player out — except that no
          // caller needs that yet, and `NULL` is unambiguous in every sport.
          where.push(`(p.injury_status IS NULL OR p.injury_status = '')`);
          break;
        case 'list': {
          const holes = rule.playerIds.map((_, i) => `?${params.length + i + 1}`).join(', ');
          params.push(...rule.playerIds);
          where.push(`p.id IN (${holes})`);
          break;
        }
        case 'experience':
          params.push(rule.atMost);
          where.push(`p.kind = 'person' AND p.experience_years <= ?${params.length}`);
          break;
        // NOT SILENTLY IGNORED. Both need facts this query does not join, and a
        // pool that quietly returned everybody would be a challenge mode that
        // was not a challenge — which nobody would notice until a draft.
        case 'playing-in-period':
        case 'depth-chart-rank':
          throw new UnresolvablePool(rule.rule);
      }
    }
  }

  // `season` IS NOT BOUND, and the first version binding it was a real bug:
  // `players` has no season column — a player row is current, not per-year —
  // so the parameter had no placeholder and D1 refused the whole statement.
  // It stays in the signature because a pool rule that needs it is coming
  // (`playing-in-period`), and because the caller's season is what decides
  // which stat lines the ranking is built from.
  void season;
  return {
    sql: `SELECT p.id, p.display_name, p.positions_json, p.pro_team, p.pro_draft_pick
            FROM players p
           WHERE ${where.join(' AND ')}
        ORDER BY p.id`,
    params,
  };
}

/** A player of the pool, as either source gives him. */
export interface PoolPlayer {
  id: string;
  name: string | null;
  positions: readonly string[];
  proTeam: string | null;
  /** Where the pro league drafted him, overall; null undrafted (`players.pro_draft_pick`). */
  proDraftPick: number | null;
}

/**
 * `poolQuery`'s answer from the directory artifact's players (ICD-S10), in
 * its order (`ORDER BY p.id`, BINARY): the same fixed conditions and the same
 * rules, in memory, and the same refusal for a rule nothing resolves yet.
 * `ingest:verify` holds the draft board built from it to the one built from
 * the table.
 */
export function poolFrom(
  pool: PlayerPool,
  sport: string,
  season: number,
  rosterPositions: readonly string[],
  players: readonly DirectoryEntry[],
): PoolPlayer[] {
  // The rules first, so a pool this build cannot resolve is refused here as
  // it is there.
  poolQuery(pool, sport, season, rosterPositions);
  const rules = pool.kind === 'filter' ? pool.rules : [];
  const holds = (p: DirectoryEntry, positions: readonly string[]) =>
    p.positions.some((x) => positions.includes(x));
  return players
    .filter((p) => {
      if (p.sport !== sport || p.status !== 'active' || p.simulated) return false;
      if (!holds(p, rosterPositions)) return false;
      return rules.every((rule) => {
        switch (rule.rule) {
          case 'positions':
            return holds(p, rule.in);
          case 'clubs':
            return p.proTeam !== null && rule.in.includes(p.proTeam);
          case 'available':
            return p.injury === null;
          case 'list':
            return rule.playerIds.includes(p.id);
          case 'experience':
            return !p.club && p.experienceYears <= rule.atMost;
          default:
            return false;
        }
      });
    })
    .map((p) => ({
      id: p.id,
      name: p.displayName,
      positions: p.positions,
      proTeam: p.proTeam,
      proDraftPick: p.proDraftPick,
    }))
    .sort((a, b) => binaryCompare(a.id, b.id));
}

/** True when this build can resolve every rule in a pool. */
export function poolIsResolvable(pool: PlayerPool): boolean {
  if (pool.kind === 'all') return true;
  return !pool.rules.some((r) => r.rule === 'playing-in-period' || r.rule === 'depth-chart-rank');
}
